import _ from "lodash";
import { ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import { isValidNumber, safeParseFloat } from "lib/number";

type IBagCol = string;

export interface IBagRow {
    c: IBagCol[];
}

export interface IBagTable {
    r: IBagRow[];
}

const isValidValue = (value: string | number | undefined): boolean => {
    return !!value && isValidNumber(value);
};

// Convert flat columns (Eg: ["Door1", "S", "100"]) into table
export const flatToTable = (flatCols: IBagCol[][]): IBagTable => {
    const rows: IBagRow[] = [];
    flatCols.forEach((col) => {
        const value = col[col.length - 1];
        // We only want to store valid number
        if (isValidValue(value)) {
            rows.push({
                c: col.slice(0, col.length - 1).concat([String(value)]),
            });
        }
    });
    return {
        r: rows,
    };
};

interface ITableMapPosition {
    rowIndex: number;
    colIndex: number;
}
export interface ITableMap {
    [key: string]: {
        children?: ITableMap;
    } & ITableMapPosition;
}
const flatToMap = (flatCols: IBagCol[], carryOverMap: ITableMap | {}, position: ITableMapPosition) => {
    if (flatCols.length === position.colIndex + 1) {
        if (flatCols.length === 1) {
            // In table structure, last element is always a value. If flatCols.length is only ONE, its value is undefined.
            carryOverMap[flatCols[position.colIndex]] = {
                ...(carryOverMap[flatCols[position.colIndex]] || {}),
                rowIndex: position.rowIndex,
                colIndex: undefined,
            };
        } else {
            carryOverMap = {
                ...carryOverMap,
                rowIndex: position.rowIndex,
                colIndex: flatCols.length - 1,
            };
        }

        return carryOverMap;
    } else if (flatCols[position.colIndex] === undefined) {
        return carryOverMap;
    }

    carryOverMap[flatCols[position.colIndex]] = flatToMap(flatCols, carryOverMap[flatCols[position.colIndex]] || {}, {
        ...position,
        colIndex: position.colIndex + 1,
    });
    return carryOverMap;
};

export const tableToMap = (rawTable: IBagTable = { r: [] }): ITableMap | {} =>
    rawTable.r.reduce((acc, row, rowIndex) => {
        acc = flatToMap(row.c, acc, { rowIndex, colIndex: 0 });
        return acc;
    }, {});

// Since memoized one only caches one result, we need to use other memoize lib.
const memoizedTableToMap = _.memoize(tableToMap);

type ITablePaths = string[];
type ITableValue = string | number | undefined;

// To save to table in bulk manner, both paths and value have to be arrays with same length.
const isValidBulkArgs = (paths: ITablePaths | ITablePaths[], value: ITableValue | ITableValue[]) => {
    // Since singular `paths` is always an array, eg: ["Jan", "D1"],
    // we need to know whether `paths` is array of array, thus `Array.isArray(paths[0])`.
    return !(
        // `value` is array, `paths` is not array of array
        (
            (Array.isArray(value) && !Array.isArray(paths[0])) ||
            // `value` is not array, `paths` is array of array
            (!Array.isArray(value) && Array.isArray(paths[0])) ||
            // `value` (array) does not have same length as `paths` (array of array)
            (Array.isArray(value) && value.length !== paths.length)
        )
    );
};

// Save into new table in single or bulk manner
export const saveToTable = (
    table: IBagTable = { r: [] },
    paths: ITablePaths | ITablePaths[],
    value: ITableValue | ITableValue[],
): IBagTable => {
    const mutatedTable = {
        r: [...table.r],
    };
    // When bulk-saving, value and paths must have same length
    if (!isValidBulkArgs(paths, value)) {
        return mutatedTable;
    }

    const bulkValues = (!Array.isArray(value) ? [value] : value) as ITableValue[];
    const bulkPaths = (!Array.isArray(paths[0]) ? [paths] : paths) as ITablePaths[];
    const positionsToRemove = {};

    const tableMap = memoizedTableToMap(table);
    bulkValues.forEach((bulkValue, index) => {
        const position: ITableMapPosition | undefined = _.get(tableMap, bulkPaths[index]);

        if (!position) {
            if (isValidValue(bulkValue)) {
                mutatedTable.r.push({
                    c: [...bulkPaths[index], String(bulkValue)],
                });
            }
        } else {
            // We only want to store valid number
            if (isValidValue(bulkValue)) {
                // When colIndex is undefined, the row only has one column. So the value should be at index 1
                const colIndex = position.colIndex === undefined ? 1 : position.colIndex;
                _.set(mutatedTable.r, [position.rowIndex, "c", colIndex], String(bulkValue));
            } else {
                positionsToRemove[position.rowIndex] = true;
            }
        }
    });
    if (Object.keys(positionsToRemove).length > 0) {
        mutatedTable.r = mutatedTable.r.filter((col, index) => !positionsToRemove[index]);
    }

    return mutatedTable;
};

type ITableGetter = (params?: ValueGetterParams | ValueSetterParams) => IBagTable | undefined;
type ITableSetter = (newTable: IBagTable, params: ValueSetterParams) => void;
type IPathsGetter = (params: ValueGetterParams | ValueSetterParams) => string[];

export const getValueByPosition = (table: IBagTable, position: ITableMapPosition) => {
    const value = _.get(table, ["r", position.rowIndex, "c", position.colIndex]);
    if (isValidNumber(value)) {
        return parseInt(value, 10);
    }

    return undefined;
};

// `params` can be undefined here so this getter can be used outside ag-grid
export const tableValueGetter = (
    bagGetter: ITableGetter | IBagTable | undefined,
    pathsGetter: IPathsGetter | string[],
    params?: ValueGetterParams,
): number | undefined => {
    // When there is group in a grid, row won't have data
    if (params && !params.data) {
        return undefined;
    }

    const table = typeof bagGetter === "function" ? bagGetter(params) : bagGetter;
    if (!table) {
        return undefined;
    }

    const tableMap = memoizedTableToMap(table);
    const paths = typeof pathsGetter === "function" ? pathsGetter(params) : pathsGetter;
    const position: ITableMapPosition | undefined = _.get(tableMap, paths);

    if (!position || position.colIndex === undefined) {
        return undefined;
    }
    return getValueByPosition(table, position);
};

export const getTableValueGetter = (bagGetter: ITableGetter | IBagTable, pathsGetter: IPathsGetter | string[]) => {
    return tableValueGetter.bind(this, bagGetter, pathsGetter);
};

export const tableValueSetter = (
    bagSetter: ITableSetter,
    bagGetter: ITableGetter | IBagTable,
    pathsGetter: IPathsGetter | string[],
    params: ValueSetterParams,
) => {
    const table = typeof bagGetter === "function" ? bagGetter(params) : bagGetter;
    const paths = typeof pathsGetter === "function" ? pathsGetter(params) : pathsGetter;
    const newTable = saveToTable(table, paths, params.newValue);

    bagSetter(newTable, params);

    return true;
};

export const getTableValueSetter = (
    bagSetter: ITableSetter,
    bagGetter: ITableGetter | IBagTable,
    pathsGetter: IPathsGetter | string[],
) => {
    return tableValueSetter.bind(this, bagSetter, bagGetter, pathsGetter);
};

export const sumBagTotal = _.memoize((bag: IBagTable) => {
    return bag.r.reduce((acc, row) => {
        return acc + safeParseFloat(row.c[row.c.length - 1], 0);
    }, 0);
});
